import { VistaAsset } from '@design-stack-vista/vista-assets-sdk'
import type { DesignPersonalizationImage, DesignPersonalizationContext, DesignPersonalizationText } from '@vp/personalization-types'
import { Brand, BrandContent, BrandData } from '@vp/react-brand'
import { waitForDimensionData } from './vistaAssetUtils'
import { RenderableTextField, PURPOSE_NAMES } from '../Schemes/store/types'

export const DIMENSION_DATA_TIMEOUT_MS = 30000

export const brandToRenderableTextFields = (brand: Brand): RenderableTextField[] => {
  const fullName = brand.facts?.people?.[0]?.fullName || brand.speculations?.people?.[0]?.fullName || undefined
  const companyName = brand.facts?.profile?.companyName || brand.speculations?.profile?.companyName || undefined

  return [
    { placeholder: 'fullname', purposeName: PURPOSE_NAMES.FULL_NAME, text: fullName },
    { placeholder: 'companyname', purposeName: PURPOSE_NAMES.COMPANY_NAME, text: companyName },
  ].filter((field) => field.text !== undefined && field.text !== '') as RenderableTextField[]
}

export const getAssetIdsFromBrand = (brand: Brand): string[] => {
  const logos =
    brand.facts?.designIdentity?.logos && brand.facts.designIdentity.logos.length > 0
      ? brand.facts.designIdentity.logos
      : brand.speculations?.designIdentity?.logos

  // TODO: This currently doesn't handle page numbers in any way, how does brand give this information to us?
  return logos ? (logos.map((logo) => logo.assetId).filter((assetId) => assetId !== undefined && assetId !== null) as string[]) : []
}

export const getTextFieldValuebyPurpose = (dpcText: DesignPersonalizationText[] | undefined, purposeName: PURPOSE_NAMES): string | undefined =>
  dpcText?.find((t) => t.textField.purposeName === purposeName)?.textField.text

export const convertVistaAssetToDpcImage = async (
  asset: VistaAsset,
  dimensionDataTimeout: number,
  defaultToPhoto: boolean,
  pageNumber?: number,
): Promise<DesignPersonalizationImage> => {
  const { height, width } = await waitForDimensionData(asset, pageNumber ?? 1, dimensionDataTimeout)
  const { data, print, webPreview } = asset

  let classifiedAsPhoto

  // getImageInfo is the fast but less accurate way to get photo or logo classification
  // TODO: consider changing this https://vistaprint.atlassian.net/browse/QSP-779
  try {
    classifiedAsPhoto = data ? (await asset.getImageInfo?.(data?.id))?.isPhoto : defaultToPhoto
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_error) {
    classifiedAsPhoto = defaultToPhoto
  }
  const purpose = classifiedAsPhoto || defaultToPhoto ? 'photo' : 'logo'

  return {
    image: {
      originalSourceUrl: asset.getUrl({ includeSignature: true, pageNum: pageNumber ?? 1 }),
      previewUrl: webPreview.getUrl({ includeSignature: true, pageNum: pageNumber ?? 1 }),
      printUrl: print.getUrl({ includeSignature: true }),
      height: height ?? 0,
      width: width ?? 0,
      sherbertAssetId: data?.id ?? '',
    },
    purpose,
  }
}

export const dpcToBrandContent = (dpc: DesignPersonalizationContext): BrandContent => {
  const brandContent: BrandContent = {}
  // For now, treat everything as facts.
  const facts: BrandData = {}

  const logos = dpc?.images?.filter((i) => i.purpose === 'logo')

  if (logos?.length) {
    facts.designIdentity = {
      ...facts?.designIdentity,
      logos: logos.map(({ image: { printUrl, previewUrl, originalSourceUrl } }) => ({
        uds: {
          printUrl,
          previewUrl,
          originalSourceUrl,
        },
      })),
    }
  }

  const companyName = getTextFieldValuebyPurpose(dpc?.text, PURPOSE_NAMES.COMPANY_NAME)

  if (companyName) {
    facts.profile = {
      ...facts?.profile,
      companyName,
    }

    brandContent.displayName = companyName
  }

  const fullName = getTextFieldValuebyPurpose(dpc?.text, PURPOSE_NAMES.FULL_NAME)

  if (fullName) {
    facts.people = [{ fullName }]
  }

  // no relevant data
  if (Object.keys(facts).length === 0) {
    return {}
  }

  brandContent.facts = facts

  return brandContent
}
